import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRealtimeTags } from 'Core/hooks';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import cx from 'classnames';
// Cloud React components
import { Grid, Alert, Typography } from '@mui/material';
import {
    Button,
    IconButton,
    Modal,
    DragAndDrop,
    Form,
    InputInline,
    Input,
    TextArea,
    Icons,
    Tooltip,
    MenuComponent,
    Label,
    Switch,
    Select
} from '@gpstrackit/gtc-react-controls';
import Tabs, { TabContainer } from 'Components/Tabs';
import TagsManage from 'Modules/.common/components/TagsManage';
import QuestionConfiguration from './components/questionConfiguration';
import { fieldTypes, fields as defaultFields } from '../../.globals/helpers';
import { HasPermission } from "Core/security";

import { NotificationManager } from 'Components/Notifications';
import './styles.scss';

//actions
import {
    reset,
    getDynamicForm,
    saveDynamicForm,
    closeDesignModal,
    openQuestionConfigurationModal
} from './redux/slice';


const signatureLimit = 5;
const pictureLimit = 5;

const DesignForm = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const designForm = Form.useForm();
    const questionsAutoScrollRef = useRef();
    const pagesAutoScrollRef = useRef();

    const {
        refresh
    } = props;

    const defaultDynamicFormValues = useMemo(() => ({
        name: null,
        description: null,
        formStatus: "inactive",
        properties: {
            allowDraft: false
        },
        pages: [
            {
                id: uuidv4(),
                title: messages['dynamicFormsDesign_page'] ? `${messages['dynamicFormsDesign_page']} 1` : "Page 1",
                fields: []
            }
        ],
        category: null
    }), [messages]);

    const user = useSelector(state => state.securityRedux.user);

    const error = useSelector(state => state.dynamicFormsDesignRedux.error);
    const id = useSelector(state => state.dynamicFormsDesignRedux.id);
    const dynamicForm = useSelector(state => state.dynamicFormsDesignRedux.dynamicForm);
    const response = useSelector(state => state.dynamicFormsDesignRedux.response);
    const open = useSelector(state => state.dynamicFormsDesignRedux.open);
    const openQuestionConfiguration = useSelector(state => state.dynamicFormsDesignRedux.openQuestionConfiguration);
    const loadingData = useSelector(state => state.dynamicFormsDesignRedux.loading);

    const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });
    const loadingTags = useSelector(state => state.catalogsRedux.loadingTags);

    const [formData, setFormData] = useState({ ...defaultDynamicFormValues });
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedPage, setSelectedPage] = useState(defaultDynamicFormValues?.pages[0]);
    const [configuratorQuestion, setConfiguratorQuestion] = useState(true);
    const [unSavedChanges, setUnsavedChanges] = useState(false);
    const [openCloseConfirmationModal, setOpenCloseConfirmationModal] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);
    const [tagsError, setTagsError] = useState(false);

    let loading = loadingTags || loadingData;

    let categoryList = [
        { id: "General", name: messages['dynamicFormsDesign_general'] },
        { id: "WorkOrder", name: messages['dynamicFormsDesign_workOrder'] }
    ]

    const translatedDefaultFields = useMemo(() => {
        return (defaultFields || []).map((field) => {
            const translated = { ...field };

            if (translated?.uiConfiguration?.name && messages[translated.uiConfiguration.name]) {
                translated.label = messages[translated.uiConfiguration.name];
            }

            const placeholderKey = `dynamicFormsDesign_placeholder${translated.type}`;
            if (messages[placeholderKey]) {
                translated.placeHolder = messages[placeholderKey];
            }

            if (translated?.type === "Switch") {
                const attrs = translated.attributes || {};
                translated.attributes = {
                    ...attrs,
                    onLabel: messages["dynamicFormsDesign_yes"] || attrs.onLabel,
                    offLabel: messages["dynamicFormsDesign_no"] || attrs.offLabel
                };
            }


            return translated;
        });
    }, [messages]);

    const getPageDisplayTitle = useCallback((page, index) => {
        const base = messages['dynamicFormsDesign_page'] || 'Page';
        const number = index + 1;
        const title = (page?.title || '').trim();
        const isDefaultPage =
            title.startsWith('Page ') &&
            !isNaN(Number(title.slice(5)));

        return isDefaultPage ? `${base} ${number}` : page.title;
    }, [messages]);

    useEffect(() => {
        if (open && id) {
            dispatch(getDynamicForm({ id }));
        }
    }, [open, id]);

    useEffect(() => {
        if (open && dynamicForm && designForm?.ref) {
            let tagIds = dynamicForm?.tagIds || [];
            let currentTags = [];
            let currentCategory = categoryList.find((item) => item?.id === dynamicForm?.category)

            tags?.forEach(tag => {
                if (tagIds?.includes(`${tag?.id}`)) {
                    currentTags.push(tag);
                }
            });

            setFormData({
                ...formData,
                ...dynamicForm,
                tags: currentTags,
                category: currentCategory || categoryList[0]
            });
            designForm?.setValues({
                name: dynamicForm?.name,
                description: dynamicForm?.description,
                allowDraft: dynamicForm?.properties?.allowDraft,
                formStatus: dynamicForm?.formStatus === "active" ? true : false,
                category: currentCategory || categoryList[0]
            });

            setSelectedPage(dynamicForm?.pages[0]);
            setCurrentTab(1);
        }
    }, [open, dynamicForm, designForm?.ref, tags]);

    useEffect(() => {
        if (open && response && !response?.error) {
            NotificationManager.success(
                messages['dynamicFormsDesign_successMessage'],
                messages['dynamicFormsDesign_success'],
                3000,
                null,
                null,
                'success',
                null
            );

            setFormData({ ...formData, id: response?.id });
            setUnsavedChanges(false);
            handleClose();
        }
    }, [open, response]);

    useEffect(() => {
        if (open && error) {
            if (response?.error) {
                if (response?.error === "There is already a form with the same name") {
                    setErrorMessage(messages['dynamicFormsDesign_duplicateFormName']);
                } else if (response?.error === "It is not possible to inactivate the form because it has active work orders assigned to it") {
                    setErrorMessage(messages['dynamicFormsDesign_errorByWorkOrders']);
                } else {
                    setErrorMessage(response?.error);
                }
            } else {
                setErrorMessage(messages['errorMessage']);
            }

            dispatch(reset({ error: false, response: null }));
        }
    }, [error, open, response]);

    const handleClose = useCallback(() => {
        if (refresh && (formData?.id || response?.id)) refresh();
        dispatch(closeDesignModal());
        dispatch(reset());
        designForm?.resetValues();
        setFormData({});
    });

    const onSubmit = useCallback((newForm) => {
        setErrorMessage("");

        const hasTags = newForm?.tags?.length > 0;
        setTagsError(!hasTags);

        if (!hasTags) {
            setCurrentTab(0);
        }

        if (!newForm?.pages?.length) {
            setErrorMessage(messages['dynamicFormsDesign_pagesMandatory']);
            return;
        }

        for (let i = 0; i < newForm?.pages?.length; i++) {
            const page = newForm?.pages[i];
            if (!page?.fields?.length) {
                setErrorMessage(messages['dynamicFormsDesign_fieldsMandatory']);
                return;
            }
        }

        if (!hasTags) {
            return;
        }

        let form = {
            ...newForm,
            tagIds: newForm?.tags?.map(t => parseInt(t?.id)),
            category: newForm?.category?.id || 'General'
        };
        dispatch(saveDynamicForm(form));
    });

    const updatePage = useCallback((newSelectedPage) => {
        setErrorMessage("");
        setSelectedPage(newSelectedPage);

        let newPages = [...formData?.pages];
        newPages = newPages?.map(newPage => {
            if (newPage?.id === newSelectedPage?.id) {
                return newSelectedPage;
            }
            return newPage;
        });

        const newFormData = { ...formData, pages: newPages };
        setFormData(newFormData);
        setUnsavedChanges(true);
    });

    const duplicateQuestion = useCallback((newQuestion, index) => {
        const currentFields = [...selectedPage?.fields];
        currentFields.splice((index + 1), 0, { ...newQuestion, id: uuidv4(), label: `${newQuestion?.label} (copy)` });
        const newSelectedPage = { ...selectedPage, fields: currentFields };

        updatePage(newSelectedPage);
    });

    const deleteQuestion = useCallback((newQuestion) => {
        const { fields } = selectedPage;
        let newItems = fields?.filter(f => f?.id != newQuestion?.id);
        const newSelectedPage = { ...selectedPage, fields: newItems };

        updatePage(newSelectedPage);
    });

    const renderFieldPreview = useCallback((item, keyItem) => {
        let placeHolder = item?.placeHolder;
        if (item?.type === fieldTypes?.Switch) {
            placeHolder = `${item?.attributes?.onLabel} | ${item?.attributes?.offLabel}`;
        }

        const field = defaultFields?.find(f => f?.type === item?.type);

        return (
            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-end"
                key={keyItem}
                wrap="nowrap"
                className='design-form-selected-field context-menu-hover-container'
            >
                <Grid item justifyContent="center">
                    <div style={{ marginRight: 7, marginBottom: 6 }} className="context-menu-hover">
                        <Icons name="drag_indicator" />
                    </div>
                </Grid>

                <Grid
                    item
                    xs
                    onClick={() => {
                        dispatch(openQuestionConfigurationModal(item));
                    }}
                >
                    <div>
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Grid item xs>
                                <Grid
                                    container
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    key={keyItem}
                                    wrap="nowrap"
                                    style={{
                                        display: 'grid'
                                    }}
                                >
                                    <Grid item xs zeroMinWidth alignItems="center" style={{ marginBottom: 5 }}>
                                        <Typography noWrap style={{ fontWeight: 'bold', fontSize: 14 }}>
                                            {item?.required ? <span className='text-danger'>*</span> : ""} {item?.label}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item>
                                {
                                    item?.help &&
                                    <Tooltip title={item?.help}>
                                        <Icons name="help" />
                                    </Tooltip>
                                }
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={1}
                        >
                            <Grid item xs>
                                <div
                                    className='design-form-selected-field-box'
                                    style={{
                                        height: item?.type === fieldTypes?.TextArea ? 50 : 25
                                    }}
                                >
                                    <div className='design-form-selected-field-box-placeholder'>
                                        {placeHolder}
                                    </div>
                                </div>
                            </Grid>
                            <Grid item>
                                {
                                    field &&
                                    <Icons className='design-form-selected-field-box-icon' size="small" name={field?.uiConfiguration?.icon} />
                                }
                            </Grid>
                        </Grid>
                    </div>
                </Grid>

                <Grid item justifyContent="flex-end">
                    <div style={{ marginLeft: 5, marginBottom: 4 }}>
                        <MenuComponent
                            disabled={loading}
                            options={[
                                messages['dynamicFormsDesign_edit'],
                                messages['dynamicFormsDesign_duplicate'],
                                messages['dynamicFormsDesign_delete']
                            ]}
                            className="context-menu-hover"
                            placement="right"
                            icon={'more_vert'}
                            onClick={(type, value) => {
                                if (type === messages['dynamicFormsDesign_edit']) {
                                    dispatch(openQuestionConfigurationModal(item));
                                } else if (type === messages['dynamicFormsDesign_duplicate']) {
                                    duplicateQuestion(item, keyItem);
                                } else if (type === messages['dynamicFormsDesign_delete']) {
                                    deleteQuestion(item);
                                }
                            }}
                            value={item}
                        />
                    </div>
                </Grid>
            </Grid>
        )
    });

    const renderAvailableField = useCallback((item, keyItem) => {
        const notAllowed = (item?.permission && !HasPermission(user, item?.permission));
        const limitReached = validateLimitFields(item.type);

        return (
            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                key={keyItem}
                className={`design-form-available-field${(notAllowed || limitReached) ? "-not-allowed" : ""}`}
                onClick={() => {
                    if (!notAllowed && !limitReached) {
                        let newItem = { ...item, id: uuidv4() };
                        if (newItem?.uiConfiguration) {
                            delete newItem.uiConfiguration;
                        }

                        if (newItem?.key) {
                            delete newItem.key;
                        }

                        if (newItem?.permission) {
                            delete newItem.permission;
                        }

                        if (newItem?.hasOwnProperty('isDragDisabled')) {
                            delete newItem.isDragDisabled;
                        }

                        const newSelectedPage = { ...selectedPage, fields: [...selectedPage.fields, ...[newItem]] };
                        updatePage(newSelectedPage);

                        if (newItem && configuratorQuestion) {
                            dispatch(openQuestionConfigurationModal(newItem));
                        }

                        let timer = setTimeout(() => {
                            questionsAutoScrollRef?.current?.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
                            timer = null;
                        }, 100);
                    }
                }}
            >
                <Grid item style={{ marginRight: 12 }}>
                    <Icons name={item?.uiConfiguration?.icon} className={`design-form-available-field-icon`} />
                </Grid>
                <Grid item xs>
                    <h3
                        style={{
                            margin: 0
                        }}
                    >
                        {messages[item?.uiConfiguration?.name]}
                    </h3>

                    {
                        !limitReached && !notAllowed &&
                        <span>{messages[item?.uiConfiguration?.description]}</span>
                    }

                    {
                        notAllowed && !limitReached &&
                        <span style={{ color: '#66bb6a', fontWeight: 'bold' }}>{messages['dynamicFormsDesign_premiumFeature']}</span>
                    }

                    {
                        !notAllowed && limitReached &&
                        <span style={{ color: '#DF1A31', fontWeight: 'bold' }}>{messages['dynamicFormsDesign_resourceFieldsLimit']}</span>
                    }
                </Grid>
            </Grid>
        );
    });

    const validateLimitFields = useCallback((type) => {
        let count = 0;
        let limitReached = false;
        const { pages: currentPages } = formData;

        currentPages?.forEach((currentPage) => {
            const { fields: currentFields } = currentPage;
            currentFields?.forEach(currentField => {
                if ([type].includes(currentField?.type)) {
                    count = count + 1;
                }
            });
        });

        if (fieldTypes.Picture === type) {
            limitReached = count >= pictureLimit;
        } else if (fieldTypes.Signature === type) {
            limitReached = count >= signatureLimit;
        }

        return limitReached;
    });

    return (
        <Modal
            open={open}
            loading={loading}
            position="right"
            size="full-screen"
            className={currentTab === 1 ? "design-form-modal" : ""}
            footer={(
                <div style={{ display: 'flex', alignItems: "center", justifyContent: 'end', width: '100%' }}>
                    <Button
                        disabled={loading}
                        style={{ marginRight: 8 }}
                        onClick={() => {
                            if (unSavedChanges) {
                                setOpenCloseConfirmationModal(true);
                            } else {
                                handleClose();
                            }
                        }}
                        variant='secondary'
                    >
                        {messages['cancel']}
                    </Button>

                    <Button
                        disabled={loading}
                        onClick={(e) => {
                            if (e) e.preventDefault();
                            setTagsError(!formData?.tags?.length);
                            designForm?.submit();
                        }}
                        variant='primary'
                    >
                        {messages['save']}
                    </Button>
                </div>
            )}
        >
            <div className='design-form-container'>
                <Tabs
                    tab={currentTab}
                    onChange={(newTab) => setCurrentTab(newTab)}
                >
                    <TabContainer
                        index={0}
                        label={messages['dynamicFormsDesign_parameters']}
                    >
                        <div>
                            <Form
                                ref={designForm.ref}
                                loading={loading}
                                onValidSubmit={() => onSubmit(formData)}
                                onChangeValues={(values) => {
                                    let newFormStatus = values?.formStatus ? "active" : "inactive";
                                    let newAllowDraft = values?.allowDraft ? true : false;
                                    const nameValue = values?.name;
                                    const sanitizedName = (typeof nameValue === 'string' && nameValue.trim().length === 0) ? undefined : nameValue;

                                    if (nameValue !== sanitizedName) {
                                        designForm?.setValues({ name: sanitizedName });
                                    }

                                    let newFormData = {
                                        ...formData,
                                        name: sanitizedName,
                                        description: values?.description,
                                        formStatus: newFormStatus,
                                        properties: {
                                            allowDraft: newAllowDraft
                                        },
                                        category: values?.category
                                    }
                                    setFormData(newFormData);
                                }}
                            >
                                <div>
                                    <Form.Item
                                        name="name"
                                        label={messages['dynamicFormsDesign_name']}
                                        rules={[{ required: true, message: 'required_field' }]}
                                    >
                                        <Input
                                            id="name"
                                            type="text"
                                            placeholder={messages['dynamicFormsDesign_name']}
                                            maxLength={300}
                                            onChange={() => setUnsavedChanges(true)}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="description"
                                        label={messages['dynamicFormsDesign_description']}
                                    >
                                        <TextArea
                                            id="description"
                                            placeholder={messages['dynamicFormsDesign_description']}
                                            maxLength={500}
                                            onChange={() => setUnsavedChanges(true)}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="category"
                                        label={messages['dynamicFormsDesign_category']}
                                        rules={[{ required: true, message: 'required_field' }]}
                                    >
                                        <Select
                                            id="category"
                                            options={HasPermission(user, 'workorders', [288]) ? categoryList : categoryList?.filter((item) => item?.id !== "WorkOrder")}
                                            placeholder={messages['dynamicFormsDesign_category']}
                                            valueField="id"
                                            textField="name"
                                            style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                                            isSearchable
                                            onChange={(value) => {
                                                setFormData({ ...formData, category: value });
                                                setUnsavedChanges(true)
                                            }}
                                        />
                                    </Form.Item>
                                    <div
                                        data-testid="create-edit-form-tags-manage"
                                        style={{ marginTop: 10, marginBottom: 10 }}
                                        className={cx("design-form-tags", tagsError ? "design-form-tags-error" : "")}
                                    >
                                        <TagsManage
                                            onlyTags={true}
                                            enableSelectAll={false}
                                            disableUntaggedSelection={true}
                                            mode="selector"
                                            menuStyle={{ height: 350 }}
                                            label={messages["dynamicFormsDesign_tags"]}
                                            placeholder={messages["search"]}
                                            value={formData?.tags || []}
                                            required
                                            requiredAsteriskPosition="start"
                                            showError={tagsError}
                                            onChange={(values) => {
                                                setFormData({ ...formData, tags: values });
                                                setTagsError(false);
                                                setErrorMessage("");
                                                setUnsavedChanges(true);
                                            }}
                                            isVisiblePill={true}
                                        />
                                        {tagsError && (
                                            <label data-testid="assistiveText-id" className="assistive-gtc-rc-text assistive-gtc-rc-text-error" style={{ marginTop: 5 }}>{messages['required_field']}</label>
                                        )}
                                    </div>
                                    <Form.Item
                                        name="allowDraft"
                                        label={messages['dynamicFormsDesign_allowDraft']}
                                    >
                                        <Switch
                                            id="allowDraft"
                                            optionLabels={[
                                                messages['dynamicFormsDesign_yes'],
                                                messages['dynamicFormsDesign_no']
                                            ]}
                                            size={"dynamic"}
                                            onChange={() => setUnsavedChanges(true)}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="formStatus"
                                        label={messages['dynamicFormsDesign_active']}
                                    >
                                        <Switch
                                            optionLabels={[
                                                messages['dynamicFormsDesign_yes'],
                                                messages['dynamicFormsDesign_no']
                                            ]}
                                            size={"dynamic"}
                                            onChange={() => setUnsavedChanges(true)}
                                        />
                                    </Form.Item>

                                    <Label>
                                        {messages['dynamicFormsDesign_openQuestionConfigurationDropped']}
                                    </Label>

                                    <div style={{ marginTop: 5, marginBottom: 12 }}>
                                        <Switch
                                            id="openQuestionConfiguration"
                                            optionLabels={[
                                                messages['dynamicFormsDesign_yes'],
                                                messages['dynamicFormsDesign_no']
                                            ]}
                                            onChange={(checked) => setConfiguratorQuestion(checked)}
                                            checked={configuratorQuestion}
                                            size={"dynamic"}
                                        />
                                    </div>
                                </div>
                            </Form>

                            {errorMessage &&
                                <Alert severity="error" onClose={() => setErrorMessage("")}>{errorMessage}</Alert>
                            }
                        </div>
                    </TabContainer>

                    <TabContainer
                        index={1}
                        label={messages['dynamicFormsDesign_questions']}
                        disabled={!formData?.name?.trim()}
                        style={{ padding: 0 }}
                    >
                        <>
                            {errorMessage &&
                                <div>
                                    <Alert severity="error" onClose={() => setErrorMessage("")}>{errorMessage}</Alert>
                                </div>
                            }

                            <Grid
                                container
                                spacing={1}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                style={{
                                    paddingBottom: 0
                                }}
                            >
                                <Grid item xs>
                                    <div
                                        style={{
                                            padding: "8px 18px 0px 24px",
                                            overflowY: "auto",
                                            height: "calc(100vh - 118px)"
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontWeight: "bold",
                                                fontSize: 18,
                                                marginTop: 8,
                                                marginBottom: 5
                                            }}
                                        >
                                            {messages['dynamicFormsDesign_pages']}

                                            <IconButton
                                                onClick={() => {
                                                    let newPages = [...formData?.pages || []];
                                                    for (let i = 0; i < newPages?.length; i++) {
                                                        const page = newPages[i];
                                                        if (!page?.fields?.length) {
                                                            setErrorMessage(messages['dynamicFormsDesign_fieldsMandatory']);
                                                            return;
                                                        }
                                                    }

                                                    const newPage = {
                                                        id: uuidv4(),
                                                        title: `${messages["dynamicFormsDesign_page"] || "Page"} ${newPages?.length + 1}`,
                                                        fields: []
                                                    };

                                                    newPages.push(newPage);
                                                    setFormData({ ...formData, pages: newPages });
                                                    setSelectedPage(newPage);
                                                    setUnsavedChanges(true);

                                                    let timer = setTimeout(() => {
                                                        pagesAutoScrollRef?.current?.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
                                                        timer = null;
                                                    }, 100);
                                                }}
                                                variant="green"
                                                size="medium"
                                                icon="add_box"
                                                isDisabled={loading}
                                                style={{
                                                    marginLeft: 3
                                                }}
                                            />
                                        </div>

                                        <div>
                                            {formData?.pages?.map((page, index) => {
                                                return (
                                                    <Grid
                                                        container
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        key={index}
                                                        className={cx("design-form-page-item", selectedPage?.id === page?.id ? "selected-page" : "")}
                                                    >
                                                        <Grid
                                                            item
                                                            xs
                                                            style={{
                                                                marginBottom: !index ? 5 : 0
                                                            }}
                                                            onClick={() => {
                                                                setErrorMessage("");
                                                                if (!selectedPage?.fields?.length) {
                                                                    setErrorMessage(messages['dynamicFormsDesign_selectPageFieldsMandatory']);
                                                                } else {
                                                                    setSelectedPage(page);
                                                                }
                                                            }}
                                                        >
                                                            {getPageDisplayTitle(page, index)}
                                                        </Grid>

                                                        <Grid item>
                                                            {
                                                                index > 0 &&
                                                                <IconButton
                                                                    onClick={() => {
                                                                        let newPages = formData?.pages?.filter(p => p?.id != page?.id);
                                                                        const newFormData = { ...formData, pages: newPages };
                                                                        setFormData(newFormData);
                                                                        setSelectedPage(newPages[0]);
                                                                        setErrorMessage("");
                                                                        setUnsavedChanges(true);
                                                                    }}
                                                                    variant="warning"
                                                                    size="xsmall"
                                                                    icon="close"
                                                                    isDisabled={loading}
                                                                />
                                                            }
                                                        </Grid>
                                                    </Grid>
                                                )
                                            })}

                                            <div ref={pagesAutoScrollRef} />
                                        </div>
                                    </div>
                                </Grid>

                                <Grid item xs={9}>
                                    <DragAndDrop
                                        containers={[
                                            {
                                                title: (
                                                    <InputInline
                                                        id="title"
                                                        type="text"
                                                        placeholder={messages['dynamicFormsDesign_title']}
                                                        value={getPageDisplayTitle(
                                                            selectedPage,
                                                            (formData?.pages || []).findIndex(p => p?.id === selectedPage?.id)
                                                        )}
                                                        onChange={(value) => {
                                                            const newSelectedPage = { ...selectedPage, title: value };
                                                            updatePage(newSelectedPage);
                                                        }}
                                                        required
                                                        requiredMessage={messages['required_field']}
                                                        maxLength={100}
                                                        valueStyle={{
                                                            fontWeight: "bold",
                                                            fontSize: 18,
                                                            marginBottom: 5
                                                        }}
                                                    />
                                                ),
                                                isDragDisabled: false,
                                                direction: "vertical",
                                                isDeletable: false,
                                                cloneItems: false,
                                                className: "",
                                                style: {
                                                    width: "67%",
                                                    padding: "8px 8px 25px 8px",
                                                    overflowY: "auto",
                                                    height: "calc(100vh - 144px)",
                                                    backgroundColor: "#f5f5f5"
                                                },
                                                items: selectedPage?.fields,
                                                footer: (
                                                    <div ref={questionsAutoScrollRef} />
                                                )
                                            },
                                            {
                                                title: messages["dynamicFormsDesign_addQuestion"],
                                                isDragDisabled: false,
                                                direction: "vertical",
                                                isDeletable: false,
                                                cloneItems: true,
                                                className: "",
                                                titleStyle: {
                                                    padding: "8px 8px 8px 12px"
                                                },
                                                style: {
                                                    width: "33%",
                                                    padding: "8px 8px 8px 8px",
                                                    overflowY: "auto",
                                                    height: `calc(100vh - ${errorMessage ? "176px" : "126px"})`
                                                },
                                                items: translatedDefaultFields?.map((field) => {
                                                    const notAllowed = (field?.permission && !HasPermission(user, field?.permission));
                                                    const limitReached = validateLimitFields(field.type);
                                                    return { ...field, isDragDisabled: (notAllowed || limitReached) };
                                                })
                                            }
                                        ]}
                                        itemRenderer={(item, keyContainer, keyItem) => {
                                            if (keyContainer == 0) {
                                                return renderFieldPreview(item, keyItem);
                                            } else {
                                                return renderAvailableField(item, keyItem);
                                            }
                                        }}
                                        onDragEnd={(result) => {
                                            let currentItems = [...result[0]?.items];
                                            let newItem = currentItems?.find(item => item?.new);

                                            if (newItem) {
                                                const limitReached = validateLimitFields(newItem?.type);
                                                const notAllowed = (newItem?.permission && !HasPermission(user, newItem?.permission));

                                                if (!notAllowed && !limitReached) {
                                                    const newItems = currentItems?.map(item => {
                                                        if (item?.uiConfiguration) {
                                                            delete item.uiConfiguration;
                                                        }

                                                        if (item?.key) {
                                                            delete item.key;
                                                        }

                                                        if (item?.new) {
                                                            delete item.new;
                                                        }

                                                        if (item?.permission) {
                                                            delete item.permission;
                                                        }

                                                        if (item?.hasOwnProperty('isDragDisabled')) {
                                                            delete item.isDragDisabled;
                                                        }

                                                        return item;
                                                    });

                                                    const newSelectedPage = { ...selectedPage, fields: newItems };
                                                    updatePage(newSelectedPage);

                                                    if (newItem && configuratorQuestion) {
                                                        dispatch(openQuestionConfigurationModal(newItems?.find(i => i?.id === newItem?.id)));
                                                    }
                                                }
                                            } else {
                                                const newSelectedPage = { ...selectedPage, fields: currentItems };
                                                updatePage(newSelectedPage);
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    </TabContainer>
                </Tabs>

                {
                    openQuestionConfiguration &&
                    <QuestionConfiguration
                        updateQuestion={(newField) => {
                            const { fields } = selectedPage;
                            let newItems = fields?.map(i => {
                                if (newField?.id === i?.id) {
                                    return newField;
                                }

                                return i;
                            });

                            const newSelectedPage = { ...selectedPage, fields: newItems };
                            updatePage(newSelectedPage);
                        }}
                    />
                }

                {
                    openCloseConfirmationModal &&
                    <Modal
                        title={messages['dynamicFormsDesign_closeConfirmationModalTitle']}
                        open={openCloseConfirmationModal}
                        loading={loading}
                        position="center"
                        size="small"
                        closeButton={false}
                        className='design-form-modal-height'
                        footer={(
                            <div style={{ display: 'flex', alignItems: "center", justifyContent: 'end', width: '100%' }}>
                                <Button
                                    disabled={loading}
                                    style={{ marginRight: 8 }}
                                    onClick={() => setOpenCloseConfirmationModal(false)}
                                    variant='secondary'
                                >
                                    {messages['back']}
                                </Button>

                                <Button
                                    disabled={loading}
                                    onClick={() => {
                                        handleClose();
                                        setOpenCloseConfirmationModal(false);
                                    }}
                                    variant='primary'
                                >
                                    {messages['close']}
                                </Button>
                            </div>
                        )}
                    >
                        <div style={{ padding: 20 }}>
                            {messages['dynamicFormsDesign_closeConfirmationModalMessage']}
                        </div>
                    </Modal>
                }
            </div>
        </Modal>
    );
}

export default DesignForm;